import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs/operators';
import { BdAppContainerService } from './app.service';
import {
  BD_FOOTER_OPTIONS_DEFAULT,
  BdAppContainerFooterComponent,
  BdFooterOptions,
} from './footer';
import { ButtonDirective, ButtonIcon } from 'primeng/button';
import { BdFeature } from '@bindoc-projects/feature-catalog-domain-lib';
import { AysMenuComponent } from '../navigation/menu/menu.component';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { BdAppContainerConfig } from './config';
import { Toolbar } from 'primeng/toolbar';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';

@Component({
  selector: 'bd-app-container',
  template: `
    <!-- Wrapper für gesamte Seite -->
    <div class="flex flex-col h-screen">
      <header class="fixed top-0 left-0 right-0 z-50 shadow-xl">
        <p-toolbar class="bd-app-toolbar">
          <ng-template #start>
            <div class="flex items-center min-w-0 flex-1">
              @if (config().menu) {
                <button
                  pButton
                  class="mr-2 sm:mr-4 shrink-0 text-2xl bg-(--app-shell-color)! border-(--app-shell-color)! text-(--app-shell-contrast-color)!"
                  (click)="toggleSidebar()"
                >
                  <i class="pi pi-bars" pButtonIcon></i>
                </button>
              }
              <a
                routerLink="/home"
                class="text-base sm:text-lg font-semibold no-underline text-inherit cursor-pointer truncate"
                >{{ title() }}</a
              >
              <ng-content select="[bd-app-left-action]"></ng-content>
            </div>
          </ng-template>
          <ng-template #center>
            <ng-content select="[bd-app-center-action]"></ng-content>
          </ng-template>
          <ng-template #end>
            <div class="flex items-center shrink-0">
              <ng-content select="[bd-app-top-right-action]"></ng-content>
            </div>
          </ng-template>
        </p-toolbar>
      </header>
      <!-- ========== END HEADER ========== -->

      <!-- ========== MAIN CONTENT ========== -->
      <div class="flex flex-col flex-1 min-h-0 min-w-0 overflow-x-hidden">
        <!-- Overlay (nur bei kleinen Bildschirmen und geöffneter Sidebar) -->
        @if (sidebarOpen && !isLargeScreen) {
          <div
            class="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
            (click)="closeSidebar()"
          ></div>
        }

        <!-- Sidebar -->
        @if (config().menu) {
          <aside
            class="fixed top-13.5 left-0 w-64 h-sidebar bg-(--p-content-background) transform transition-transform duration-300 ease-in-out z-50 shadow-lg overflow-y-auto flex flex-col"
            [ngClass]="{
              '-translate-x-full': !sidebarOpen,
              'translate-x-0': sidebarOpen,
            }"
          >
            <div class="flex-1 overflow-y-auto">
              <ays-menu [features]="features()"></ays-menu>
            </div>

            <!-- Sidebar foot: copyright + info popover (legal links) -->
            <div
              class="border-t border-(--p-content-border-color) px-3.5 py-2 flex items-center gap-2.5"
            >
              <span
                class="flex-1 text-[11px] leading-4 text-(--p-text-muted-color) truncate"
                >&copy; BinDoc GmbH {{ currentYear }}</span
              >
              <button
                type="button"
                class="bd-footer-info-trigger"
                pTooltip="Rechtliches"
                tooltipPosition="top"
                aria-label="Rechtliches"
                (click)="footerPopover.toggle($event)"
              >
                <i class="pi pi-info-circle text-sm"></i>
              </button>
              <p-popover #footerPopover>
                <bd-footer [options]="footerOptions()"></bd-footer>
              </p-popover>
            </div>
          </aside>
        }

        <!-- Main Content -->
        <main
          class="bd-app-main flex-1 pt-13.5 bg-(--p-content-background) transition-all duration-300 w-full flex flex-col min-h-0"
          [ngClass]="{
            'lg:pl-64': sidebarOpen && isLargeScreen && config().menu,
          }"
        >
          <ng-content select="[bd-app-content]"></ng-content>
        </main>
      </div>
      <!-- ========== END MAIN CONTENT ========== -->
    </div>
  `,
  styles: [
    `
      :host {
        --bd-app-header-height: 3.375rem;
      }

      .h-sidebar {
        height: calc(100vh - 54px);
      }

      :host ::ng-deep .bd-app-toolbar {
        background: var(--app-shell-color);
        color: var(--app-shell-contrast-color);
        border: none;
        border-radius: 0;
        padding: 0 0.5rem;
        min-height: 54px;
        height: 54px;
      }

      @media (min-width: 640px) {
        :host ::ng-deep .bd-app-toolbar {
          padding: 0 1rem;
        }
      }

      /* Prevent toolbar children from wrapping onto a new row, which would
         break the fixed 54px header height on small viewports. PrimeNG 21
         renders the host element with BOTH the host class ".bd-app-toolbar"
         and the PrimeNG class ".p-toolbar" — same node — so the selector
         below uses compound classes (no space) rather than a descendant
         combinator. */
      :host ::ng-deep .bd-app-toolbar.p-toolbar {
        flex-wrap: nowrap;
        min-width: 0;
      }

      :host ::ng-deep .bd-app-toolbar .p-toolbar-start {
        min-width: 0;
        flex: 0 1 auto;
        overflow: hidden;
      }

      /* Center section hosts the global search bar; let it take the slack
         between the (truncating) title and the right-hand actions. */
      :host ::ng-deep .bd-app-toolbar .p-toolbar-center {
        min-width: 0;
        flex: 1 1 auto;
        display: flex;
        justify-content: center;
        padding: 0 0.5rem;
      }

      :host ::ng-deep .bd-app-toolbar .p-toolbar-end {
        flex-shrink: 0;
      }

      .bd-footer-info-trigger {
        width: 24px;
        height: 24px;
        border-radius: 6px;
        background: var(--p-content-hover-background);
        color: var(--p-text-muted-color);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border: none;
        padding: 0;
      }

      .bd-footer-info-trigger:hover {
        background: var(--p-content-border-color);
        color: var(--p-text-color);
      }

      /* Let routed components fill the remaining vertical space */
      :host ::ng-deep .bd-app-main > *:not(router-outlet) {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
      }
    `,
  ],
  imports: [
    ButtonDirective,
    ButtonIcon,
    BdAppContainerFooterComponent,
    AysMenuComponent,
    NgClass,
    RouterLink,
    Toolbar,
    Popover,
    Tooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdAppContainerComponent implements OnInit {
  readonly footerOptions = input<BdFooterOptions>(BD_FOOTER_OPTIONS_DEFAULT);
  readonly features = input<BdFeature<string>[]>();

  readonly config = input<BdAppContainerConfig>({
    menu: true,
  });

  private readonly _appService = inject(BdAppContainerService);
  private readonly _destroyRef = inject(DestroyRef);

  public readonly title = signal('');
  public readonly currentYear = new Date().getFullYear();
  public isLargeScreen =
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true;
  public sidebarOpen = this.isLargeScreen;

  constructor() {
    this._appService.title$
      .pipe(startWith(this._appService.title), takeUntilDestroyed(this._destroyRef))
      .subscribe((title) => this.title.set(title));
  }

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window === 'undefined') return;
    this.isLargeScreen = window.innerWidth >= 1024;
    if (!this.isLargeScreen && !this.sidebarOpen) {
      this.sidebarOpen = false;
      this._updateDocumentStyles();
    }
  }

  ngOnInit(): void {
    this._updateDocumentStyles();
  }

  public toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
    this._updateDocumentStyles();
  }

  closeSidebar() {
    this.sidebarOpen = false;
    this._updateDocumentStyles();
  }

  private _updateDocumentStyles(): void {
    if (typeof document === 'undefined') return;
    if (this.sidebarOpen) {
      document.documentElement.style.setProperty(
        `--bd-app-container-width`,
        'calc(100vw - 256px)',
      );
    } else {
      document.documentElement.style.removeProperty('--bd-app-container-width');
    }
  }
}
